import { useState, useEffect, useRef } from "react";
import { Sparkles, Plus, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { DietaryTags } from "./DietaryTags";
import { useTranslation } from "react-i18next";
import mealPlaceholder from "../assets/meal-placeholder.png";
import { useNewItems } from "@/hooks/useNewItems";
import type { CompanyType } from "@/types/company";
import type { ItemType } from "@/types/item";

interface NewItemsProps {
  selectedCompany: CompanyType;
  onItemClick: (item: ItemType) => void;
  onAddToCart: (item: ItemType) => void;
}

export function NewItems({ selectedCompany, onItemClick }: NewItemsProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const [isLastItemVisible, setIsLastItemVisible] = useState(false);
  const itemRefs = useRef<HTMLDivElement[]>([]);

  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir(i18n.language) === "rtl";
  const language = i18n.language;

  const { data: newItems, isLoading } = useNewItems({
    companyId: selectedCompany?.id,
    limit: 10,
  });

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.setAttribute("dir", isRTL ? "rtl" : "ltr");

      const container = scrollContainerRef.current;
      const handleScroll = () => {
        if (!scrollContainerRef.current) return;

        const container = scrollContainerRef.current;
        const itemWidth =
          (container.children[0] as HTMLElement)?.offsetWidth || 0;
        const gap = 24;
        const scrollLeft = Math.abs(container.scrollLeft);
        const itemWithGap = itemWidth + gap;
        const visibleCount = getVisibleItems();

        if (itemWithGap === 0) return;

        const totalPages = Math.ceil(newItems?.length || 0 / visibleCount);
        const maxScrollLeft =
          (newItems?.length || 0 - visibleCount) * itemWithGap;

        if (isLastItemVisible) {
          return;
        }

        const estimatedPage = Math.floor(
          scrollLeft / (itemWithGap * visibleCount)
        );
        const clampedPage = Math.min(estimatedPage, totalPages - 1);
        // if (totalPages === 4 && index === 2) {
        //   setPageIndex(3);
        // }
        // if (totalPages === 10 && index === 9) {
        //   setPageIndex(9);
        // } else {
        //   setPageIndex(clampedPage);
        // }
      };

      container.addEventListener("scroll", handleScroll);

      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [language, isRTL, newItems?.length]);

  useEffect(() => {
    let observer: IntersectionObserver;

    const setupObserver = () => {
      if (!lastItemRef.current || !scrollContainerRef.current) return;

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const isVisible = entry.isIntersecting;
          setIsLastItemVisible(isVisible);

          if (!isVisible) return;

          if (isVisible) {
            const visibleCount = getVisibleItems();
            const totalPages = Math.ceil(newItems?.length || 0 / visibleCount);
            setPageIndex(totalPages - 1);
          }
        },
        {
          root: scrollContainerRef.current,
          threshold: 1,
        }
      );

      observer.observe(lastItemRef.current);
    };

    const raf = requestAnimationFrame(setupObserver);

    return () => {
      cancelAnimationFrame(raf);
      if (observer) observer.disconnect();
    };
  }, [newItems, language, isRTL]);

  useEffect(() => {
    const visibleCount = getVisibleItems();
    const totalPages = Math.ceil(newItems?.length || 0 / visibleCount);

    if (!scrollContainerRef.current || newItems?.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.left - b.boundingClientRect.left
          );

        if (isLastItemVisible) {
          setPageIndex(totalPages - 1);
          return;
        }

        if (visibleEntries.length > 0) {
          const firstVisibleIndex = itemRefs.current.findIndex(
            (ref) => ref === visibleEntries[0].target
          );
          const page = Math.floor(firstVisibleIndex / visibleCount);
          setPageIndex(Math.min(page, totalPages - 1));
        }
      },
      {
        root: scrollContainerRef.current,
        threshold: 0.6,
      }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      if (observer) observer.disconnect();
    };
  }, [newItems, language, isRTL, isLastItemVisible]);

  const getVisibleItems = () => {
    if (typeof window !== "undefined") {
      if (window.innerWidth >= 1024) return 3;
      if (window.innerWidth >= 640) return 2;
      return 1;
    }
    return 3;
  };
  const scrollToIndex = (index: number) => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const itemWidth = (container.children[0] as HTMLElement)?.offsetWidth || 0;
    const gap = 24;
    const visibleCount = getVisibleItems();
    const totalItems = newItems?.length || 0;
    const totalPages = Math.ceil(totalItems / visibleCount);
    const clampedPage = Math.min(index, totalPages - 1);
    const maxScrollIndex = totalItems - visibleCount;
    const scrollIndex = clampedPage * visibleCount;
    const finalIndex = Math.min(scrollIndex, maxScrollIndex);
    const scrollTo = finalIndex * (itemWidth + gap);

    container.scrollTo({
      left: isRTL ? -scrollTo : scrollTo,
      behavior: "smooth",
    });

    setPageIndex(clampedPage);
  };

  const nextSlide = () => {
    const totalPages = Math.ceil(newItems?.length || 0 / getVisibleItems());
    const nextIndex = Math.min(pageIndex + 1, totalPages - 1);
    scrollToIndex(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex = Math.max(pageIndex - 1, 0);
    scrollToIndex(prevIndex);
  };

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-8 sm:py-12">
          <div className="flex items-center mb-6 sm:mb-8">
            <div className="animate-pulse">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded me-3"></div>
            </div>
            <div className="animate-pulse">
              <div className="w-32 sm:w-48 h-6 sm:h-8 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div className="flex space-x-6 overflow-hidden">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse flex-shrink-0 w-80">
                <div className="bg-gray-200 h-40 sm:h-48 rounded-lg mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (newItems && newItems.length === 0) {
    return null;
  }

  const showNavigation = newItems && newItems.length > getVisibleItems();

  if (newItems && newItems.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full me-3 sm:me-4">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900">
                {t("home.new_items")}
              </h2>
              <p className="text-gray-800 mt-1 text-sm sm:text-base">
                {t("home.new_items_subtitle")}
              </p>
            </div>
          </div>
          {showNavigation && (
            <div
              className={`flex items-center gap-2 ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <button
                onClick={isRTL ? nextSlide : prevSlide}
                className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-all duration-200 text-gray-800 hover:text-primary"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <button
                onClick={isRTL ? prevSlide : nextSlide}
                className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-all duration-200 text-gray-800 hover:text-primary"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        <div className="relative carousel-wrapper">
          <div
            ref={scrollContainerRef}
            className={`flex overflow-x-auto scrollbar-hide scroll-smooth relative ${
              isRTL ? "gap-x-reverse gap-4 sm:gap-6" : "gap-4 sm:gap-6"
            }`}
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              overflowY: "visible",
            }}
          >
            {newItems?.map((item, idx) => (
              <div
                key={item.id}
                ref={(el) => {
                  itemRefs.current[idx] = el as HTMLDivElement;
                  if (idx === newItems?.length - 1) {
                    lastItemRef.current = el;
                  }
                }}
                className="group relative bg-white rounded-xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-300 transform -translate-y-2 flex-shrink-0 w-72 sm:w-80"
              >
                <div className="absolute top-2 start-2 sm:top-4 sm:start-4 z-10">
                  <div className="flex items-center bg-gradient-to-r from-green-500 to-emerald-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold shadow-lg">
                    <Clock className="w-3 h-3 me-1" />
                    {t("home.new")}
                  </div>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onItemClick(item);
                  }}
                  className="absolute top-2 end-2 sm:top-4 sm:end-4 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700 hover:text-primary p-1.5 sm:p-2 rounded-full shadow-lg transition-all duration-200 transform hover:scale-110 opacity-0 group-hover:opacity-100"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>

                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img
                    src={item.image || mealPlaceholder}
                    alt={item.name}
                    onClick={() => onItemClick(item)}
                    className="w-full h-full object-cover z-0 group-hover:scale-110 transition-transform duration-500 cursor-pointer"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10"></div>
                </div>

                <div
                  className="p-4 sm:p-6 cursor-pointer"
                  onClick={() => onItemClick(item)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 group-hover:text-primary transition-colors line-clamp-1 item-name">
                      {item.name}
                    </h3>
                    <span className="text-lg sm:text-2xl font-bold text-primary ms-2">
                      ₪{item.price}
                    </span>
                  </div>

                  {item.description && (
                    <p className="text-gray-800 mb-3 sm:mb-4 line-clamp-2 text-xs sm:text-sm">
                      {item.description}
                    </p>
                  )}

                  <DietaryTags
                    foodName={item.name}
                    description={item.description}
                    showTags={true}
                    className="mb-2"
                  />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <span className="bg-gray-100 px-2 py-1 rounded-full text-xs category-name">
                        {item.categoryName}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemClick(item);
                      }}
                      className="text-primary hover:text-primary/80 text-xs sm:text-sm font-medium transition-colors"
                    >
                      {t("item.view_details")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {showNavigation && (
            <div className="flex justify-center mt-6 gap-2">
              {Array.from({
                length: Math.ceil(newItems?.length || 0 / getVisibleItems()),
              }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    pageIndex === index
                      ? "bg-primary w-6"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
